<script>
  import { onMount } from "svelte";
  import { authFetch } from "../authService";
  import { getApiUrl } from "../api";
  import { t } from "../i18nStore";
  import AdminAITaskConfigs from "./AdminAITaskConfigs.svelte";

  let activeTab = "models"; // 'models' or 'tasks'
  let models = [];
  let failures = [];
  let isLoading = true;
  let isDiscovering = false;
  let isDiscoveringDeepSeek = false;
  let errorMsg = "";
  let successMsg = "";

  async function fetchAIModelsData() {
    try {
      isLoading = true;
      errorMsg = "";
      const res = await authFetch(getApiUrl("/admin/ai-models"));
      if (res.ok) {
        const data = await res.json();
        models = (data.models || []).sort((a, b) => b.priority - a.priority);
        failures = data.failures || [];
      } else {
        errorMsg = "Failed to load AI Models data.";
      }
    } catch (e) {
      errorMsg = "Error connecting to server: " + e.message;
    } finally {
      isLoading = false;
    }
  }

  async function toggleModel(model) {
    try {
      errorMsg = "";
      successMsg = "";
      const res = await authFetch(getApiUrl(`/admin/ai-models/${model.id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isEnabled: !model.isEnabled })
      });
      if (res.ok) {
        successMsg = `Model status updated successfully.`;
        await fetchAIModelsData();
      } else {
        errorMsg = "Failed to update model status.";
      }
    } catch (e) {
      errorMsg = "Error: " + e.message;
    }
  }

  async function updatePriority(model, newPriority) {
    try {
      errorMsg = "";
      successMsg = "";
      const priorityVal = parseInt(newPriority);
      if (isNaN(priorityVal)) return;

      const res = await authFetch(getApiUrl(`/admin/ai-models/${model.id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isEnabled: model.isEnabled, priority: priorityVal })
      });
      if (res.ok) {
        successMsg = "Priority updated successfully.";
        await fetchAIModelsData();
      } else {
        errorMsg = "Failed to update model priority.";
      }
    } catch (e) {
      errorMsg = "Error: " + e.message;
    }
  }

  async function runDiscovery() {
    try {
      isDiscovering = true;
      errorMsg = "";
      successMsg = "";
      const res = await authFetch(getApiUrl("/admin/ai-models/discover"), {
        method: "POST"
      });
      if (res.ok) {
        successMsg = "Discovery triggered in the background. Refreshing in 3 seconds...";
        setTimeout(async () => {
          await fetchAIModelsData();
          isDiscovering = false;
        }, 3000);
      } else {
        errorMsg = "Failed to trigger discovery.";
        isDiscovering = false;
      }
    } catch (e) {
      errorMsg = "Error: " + e.message;
      isDiscovering = false;
    }
  }

  async function runDeepSeekDiscovery() {
    try {
      isDiscoveringDeepSeek = true;
      errorMsg = "";
      successMsg = "";
      const res = await authFetch(getApiUrl("/admin/ai-models/discover-deepseek"), {
        method: "POST"
      });
      if (res.ok) {
        successMsg = "DeepSeek discovery triggered in the background. Refreshing in 3 seconds...";
        setTimeout(async () => {
          await fetchAIModelsData();
          isDiscoveringDeepSeek = false;
        }, 3000);
      } else {
        errorMsg = "Failed to trigger DeepSeek discovery.";
        isDiscoveringDeepSeek = false;
      }
    } catch (e) {
      errorMsg = "Error: " + e.message;
      isDiscoveringDeepSeek = false;
    }
  }

  async function clearFailure(modelId) {
    try {
      errorMsg = "";
      successMsg = "";
      const res = await authFetch(getApiUrl(`/admin/ai-models/failures/${modelId}`), {
        method: "DELETE"
      });
      if (res.ok) {
        successMsg = "Failure record cleared successfully.";
        await fetchAIModelsData();
      } else {
        errorMsg = "Failed to clear failure record.";
      }
    } catch (e) {
      errorMsg = "Error: " + e.message;
    }
  }

  onMount(async () => {
    await fetchAIModelsData();
  });
</script>

<div class="container">
  <div class="header">
    <h1>{activeTab === 'tasks' ? $t('admin.ai_tasks.title') : $t('admin.ai_models.title')}</h1>
    {#if activeTab === 'models'}
      <div class="action-buttons">
        <button class="btn btn-primary" on:click={runDiscovery} disabled={isDiscovering || isDiscoveringDeepSeek}>
          {#if isDiscovering}
            {$t('admin.ai_models.discovering')}
          {:else}
            {$t('admin.ai_models.discover_btn')}
          {/if}
        </button>
        <button class="btn btn-secondary" on:click={runDeepSeekDiscovery} disabled={isDiscovering || isDiscoveringDeepSeek}>
          {#if isDiscoveringDeepSeek}
            {$t('admin.ai_models.discovering_deepseek')}
          {:else}
            {$t('admin.ai_models.discover_deepseek_btn')}
          {/if}
        </button>
      </div>
    {/if}
  </div>

  <div class="nav-tabs">
    <button
      class="nav-tab {activeTab === 'models' ? 'active' : ''}"
      on:click={() => activeTab = 'models'}
    >
      📋 {$t('admin.ai_tasks.tab_models')}
    </button>
    <button
      class="nav-tab {activeTab === 'tasks' ? 'active' : ''}"
      on:click={() => activeTab = 'tasks'}
    >
      ⚙️ {$t('admin.ai_tasks.tab_tasks')}
    </button>
  </div>

  {#if activeTab === 'tasks'}
    <AdminAITaskConfigs />
  {:else}

  {#if errorMsg}
    <div class="alert alert-danger">{errorMsg}</div>
  {/if}
  {#if successMsg}
    <div class="alert alert-success">{successMsg}</div>
  {/if}

  {#if isLoading}
    <div class="loading">{$t('common.loading')}</div>
  {:else}
    <div class="section">
      <h2>{$t('admin.ai_models.models_title')}</h2>
      {#if models.length === 0}
        <p class="no-data">{$t('admin.ai_models.no_models')}</p>
      {:else}
        <table class="table">
          <thead>
            <tr>
              <th>{$t('admin.ai_models.table_name')}</th>
              <th>{$t('admin.ai_models.table_provider')}</th>
              <th>{$t('admin.ai_models.table_display')}</th>
              <th>{$t('admin.ai_models.table_priority')}</th>
              <th>{$t('admin.ai_models.table_status')}</th>
              <th>{$t('admin.ai_models.table_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {#each models as model}
              <tr class={model.isEnabled ? "" : "disabled-row"}>
                <td class="model-id">{model.id}</td>
                <td><span class="badge badge-{model.provider}">{model.provider}</span></td>
                <td>{model.displayName}</td>
                <td>
                  <input
                    type="number"
                    value={model.priority}
                    on:blur={(e) => updatePriority(model, e.target.value)}
                    class="priority-input"
                    min="0"
                  />
                </td>
                <td>
                  <span class="status-indicator {model.isEnabled ? 'active' : 'inactive'}">
                    {model.isEnabled ? $t('admin.ai_models.status_enabled') : $t('admin.ai_models.status_disabled')}
                  </span>
                </td>
                <td>
                  <button
                    class="btn btn-sm {model.isEnabled ? 'btn-danger' : 'btn-success'}"
                    on:click={() => toggleModel(model)}
                  >
                    {model.isEnabled ? $t('admin.ai_models.btn_disable') : $t('admin.ai_models.btn_enable')}
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>

    <div class="section">
      <h2>{$t('admin.ai_models.failed_title')}</h2>
      {#if failures.length === 0}
        <p class="no-data">{$t('admin.ai_models.no_failed_models')}</p>
      {:else}
        <table class="table">
          <thead>
            <tr>
              <th>{$t('admin.ai_models.table_name')}</th>
              <th>{$t('admin.ai_models.table_failed_at')}</th>
              <th>{$t('admin.ai_models.table_reason')}</th>
              <th>{$t('admin.ai_models.table_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {#each failures as fail}
              <tr>
                <td class="model-id">{fail.id}</td>
                <td>{new Date(fail.failTime).toLocaleString()}</td>
                <td class="reason-cell">{fail.reason}</td>
                <td>
                  <button class="btn btn-sm btn-warning" on:click={() => clearFailure(fail.id)}>
                    {$t('admin.ai_models.btn_clear_fail')}
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>
  {/if}
  {/if}
</div>

<style>
  .nav-tabs {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    border-bottom: 2px solid #dee2e6;
  }
  .nav-tab {
    background: none;
    border: none;
    padding: 0.75rem 1.5rem;
    font-size: 1.05rem;
    font-weight: 600;
    color: #6c757d;
    cursor: pointer;
    border-bottom: 3px solid transparent;
    transition: all 0.2s;
  }
  .nav-tab:hover {
    color: #007bff;
  }
  .nav-tab.active {
    color: #007bff;
    border-bottom: 3px solid #007bff;
  }
  .container {
    max-width: 1200px;
    margin: 2rem auto;
    padding: 0 1rem;
    text-align: left;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 2px solid #eaeaea;
    padding-bottom: 1rem;
    margin-bottom: 1.5rem;
  }
  .action-buttons {
    display: flex;
    gap: 0.5rem;
  }
  h1, h2 {
    margin: 0;
    color: #333;
  }
  h2 {
    font-size: 1.4rem;
    margin-bottom: 1rem;
  }
  .section {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    padding: 1.5rem;
    margin-bottom: 2rem;
  }
  .btn {
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
    transition: background-color 0.2s;
  }
  .btn-primary { background-color: #007bff; color: white; }
  .btn-primary:hover { background-color: #0056b3; }
  .btn-secondary { background-color: #6c757d; color: white; }
  .btn-secondary:hover { background-color: #545b62; }
  .btn-success { background-color: #28a745; color: white; }
  .btn-success:hover { background-color: #218838; }
  .btn-danger { background-color: #dc3545; color: white; }
  .btn-danger:hover { background-color: #c82333; }
  .btn-warning { background-color: #ffc107; color: #212529; }
  .btn-warning:hover { background-color: #e0a800; }
  .btn:disabled { opacity: 0.6; cursor: not-allowed; }
  
  .alert {
    padding: 0.75rem 1.25rem;
    border-radius: 4px;
    margin-bottom: 1rem;
    font-weight: 500;
  }
  .alert-danger { background-color: #f8d7da; color: #721c24; }
  .alert-success { background-color: #d4edda; color: #155724; }

  .loading {
    text-align: center;
    font-size: 1.2rem;
    color: #666;
    padding: 2rem;
  }
  .no-data {
    color: #888;
    font-style: italic;
  }

  .table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 0.5rem;
  }
  .table th, .table td {
    padding: 0.75rem;
    border-bottom: 1px solid #dee2e6;
    text-align: left;
    vertical-align: middle;
  }
  .table th {
    background-color: #f8f9fa;
    color: #495057;
    font-weight: 600;
  }
  .disabled-row {
    background-color: #fcfcfc;
    color: #999;
  }
  .model-id {
    font-family: monospace;
    font-weight: bold;
    color: #333;
  }
  .disabled-row .model-id {
    color: #999;
  }

  .badge {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.8rem;
    font-weight: bold;
    text-transform: uppercase;
  }
  .badge-gemini { background-color: #e3f2fd; color: #0d47a1; }
  .badge-deepseek { background-color: #efebe9; color: #4e342e; }

  .status-indicator {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: bold;
  }
  .status-indicator.active { background-color: #d4edda; color: #155724; }
  .status-indicator.inactive { background-color: #e2e3e5; color: #383d41; }

  .priority-input {
    width: 60px;
    padding: 0.25rem;
    border: 1px solid #ccc;
    border-radius: 4px;
  }
  .reason-cell {
    font-size: 0.9rem;
    color: #d93025;
    word-break: break-all;
    max-width: 300px;
  }
</style>
